import { AxiosResponse } from "axios";
import { Module } from "vuex";
import { SET_ERRORMSG, SET_VERIFICATION, SET_VERIFICATIONS } from "../mutation_types";
import {
    CLEAR_VERIFICATIONS_ISLOADING,
    DELETE_VERIFICATION,
    GET_VERIFICATIONS,
    TOGGLE_VERIFICATION_IS_ACTIVE,
    UPDATE_VERIFICATION_STATUS,
    VERIFY_TRANSACTION,
} from "../action_types";
import { StateObject } from "@/dtos/state.dto";
import VerificationService from "@/services/verification";
import {
    GetVerifications,
    ToggleVerificationIsActive,
    UpdateVerificationStatus,
    VerifyTransaction,
} from "@/dtos/verification.dto";

const verification: Module<any, {}> = {
    state: {
        verification: { data: null, isLoading: false },
        verifications: { data: null, metaData: null, isLoading: false },
    },
    getters: {
        verification: (state) => state.verification,
        verifications: (state) => state.verifications,
    },
    mutations: {
        [SET_VERIFICATION]: (state: { verification: StateObject }, payload: any) => {
            if (payload.data !== undefined) state.verification.data = payload.data;
            state.verification.isLoading = payload.isLoading;
        },
        [SET_VERIFICATIONS]: (state: { verifications: StateObject }, payload: any) => {
            if (payload.data !== undefined) state.verifications.data = payload.data;
            if (payload.metaData !== undefined) state.verifications.metaData = payload.metaData;
            state.verifications.isLoading = payload.isLoading;
        },
    },
    actions: {
        [GET_VERIFICATIONS]: async ({ commit }, payload: GetVerifications) => {
            commit(SET_VERIFICATIONS, { data: null, isLoading: true });
            const result: AxiosResponse = await VerificationService.getVerifications(
                payload ?? {},
            );
            if (result?.data?.status) {
                commit(SET_VERIFICATIONS, {
                    data: result.data.data,
                    metaData: result.data.metaData,
                    isLoading: false,
                });
                return true;
            }
            commit(SET_VERIFICATIONS, { isLoading: false });
            if (result) commit(SET_ERRORMSG, result.data.msg);
            return false;
        },
        // Manual verification. Returns the verified record on success (so the
        // modal can show the result) or false on failure.
        [VERIFY_TRANSACTION]: async ({ commit }, payload: VerifyTransaction) => {
            commit(SET_VERIFICATION, { data: null, isLoading: true });
            const result: AxiosResponse = await VerificationService.verifyTransaction(payload);
            if (result?.data?.status) {
                commit(SET_VERIFICATION, { data: result.data.data, isLoading: false });
                return result.data.data;
            }
            commit(SET_VERIFICATION, { isLoading: false });
            if (result) commit(SET_ERRORMSG, result.data.msg);
            return false;
        },
        [UPDATE_VERIFICATION_STATUS]: async (
            { commit, state },
            payload: UpdateVerificationStatus,
        ) => {
            commit(SET_VERIFICATION, { isLoading: true });
            const result: AxiosResponse = await VerificationService.updateStatus(payload);
            if (result?.data?.status) {
                // Patch the row in the loaded list so the table reflects the new
                // status without a full refetch.
                if (state.verifications.data) {
                    const idx = state.verifications.data.findIndex(
                        (v: any) => v.id === result.data.data.id,
                    );
                    if (idx >= 0) {
                        state.verifications.data[idx] = {
                            ...state.verifications.data[idx],
                            ...result.data.data,
                        };
                    }
                }
                commit(SET_VERIFICATION, { data: result.data.data, isLoading: false });
                return true;
            }
            commit(SET_VERIFICATION, { isLoading: false });
            if (result) commit(SET_ERRORMSG, result.data.msg);
            return false;
        },
        [TOGGLE_VERIFICATION_IS_ACTIVE]: async (
            { commit, state },
            payload: ToggleVerificationIsActive,
        ) => {
            commit(SET_VERIFICATION, { isLoading: true });
            const result: AxiosResponse = await VerificationService.toggleIsActive(payload);
            if (result?.data?.status) {
                if (state.verifications.data) {
                    const idx = state.verifications.data.findIndex(
                        (v: any) => v.id === result.data.data.id,
                    );
                    if (idx >= 0)
                        state.verifications.data[idx].isActive = result.data.data.isActive;
                }
                commit(SET_VERIFICATION, { data: result.data.data, isLoading: false });
                return true;
            }
            commit(SET_VERIFICATION, { isLoading: false });
            if (result) commit(SET_ERRORMSG, result.data.msg);
            return false;
        },
        [DELETE_VERIFICATION]: async ({ commit }, payload: string) => {
            commit(SET_VERIFICATION, { isLoading: true });
            const result: AxiosResponse = await VerificationService.deleteVerification(payload);
            if (result?.data?.status) {
                commit(SET_VERIFICATION, { data: result.data.data, isLoading: false });
                return true;
            }
            commit(SET_VERIFICATION, { isLoading: false });
            if (result) commit(SET_ERRORMSG, result.data.msg);
            return false;
        },
        [CLEAR_VERIFICATIONS_ISLOADING]: ({ commit }) => {
            commit(SET_VERIFICATION, { isLoading: false });
            commit(SET_VERIFICATIONS, { isLoading: false });
        },
    },
};

export default verification;
